import React, { Dispatch, useMemo, useState } from 'react';

import { RatchetModels, RatchetModelsEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';

import { isEmpty, isNil, isUndefined } from 'lodash';
import { AddConversionModelDefinitionsTable } from 'src/components/AddConversionModelDefinitionsTable';
import { ContractTermSection } from 'src/components/shared/ContractTermSection';
import { ServiceId } from 'src/constants';
import { AppContext } from 'src/contexts/appContext';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';
import { getThresholdDisplayName } from 'src/util/thresholdUtil';

import {
    COMBINED_PRIME_UMG_FLOOR_CONVERSION_MODELS,
    COMBINED_PRIME_WMG_CONVERSION_MODELS,
    PRIME_COMBINED_CONVERSION_UMG,
    PRIME_COMBINED_CONVERSION_WMG,
    conversionModelsThresholdsMap,
    groupConversionModels
} from './conversionModelThresholds';

interface IAddConversionModelProps {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    conversionModels: RatchetModels;
}

export const AddConversionModel = ({
    rateCardValues,
    dispatchRateCardValues,
    conversionModels
}: IAddConversionModelProps) => {
    const { metadata } = React.useContext(AppContext);
    const services = metadata.serviceMapping;
    const serviceName = rateCardValues.serviceId === services[2].serviceId ? 'Prime' : 'AMF';
    const rateCardConversionModels =
        rateCardValues.serviceId === ServiceId.Prime
            ? rateCardValues.primeContractTerms?.costPerPlay?.conversions
            : rateCardValues.amfContractTerms?.adRevShare?.conversions;
    const [isToggled, setIsToggled] = useState(!isEmpty(rateCardConversionModels));
    const groupedConversionModels = useMemo(() => groupConversionModels(conversionModels), [conversionModels]);

    const handleToggle = (): void => {
        // If toggling off, set to undefined
        // If toggling on, set to first conversion model
        const conversionModelsToAdd = isToggled ? undefined : Object.values(groupedConversionModels)[0];
        dispatchRateCardValues({
            type: 'set_conversion_model',
            conversionModels: conversionModelsToAdd
        });
        setIsToggled((prevIsToggled) => !prevIsToggled);
    };

    const handleConversionModelChange = (conversionModel: string): void => {
        const conversionsModelToAdd = groupedConversionModels[conversionModel];
        dispatchRateCardValues({
            type: 'set_conversion_model',
            conversionModels: conversionsModelToAdd
        });
    };

    return (
        <ContractTermSection
            title={`${serviceName} conversions`}
            subTitle="Enable variable rates based on conversion performance"
            isToggled={isToggled}
            handleToggle={handleToggle}
        >
            {isToggled ? (
                <>
                    <Select
                        placeholder="Conversion model"
                        value={
                            isNil(rateCardConversionModels)
                                ? 'No conversion model'
                                : COMBINED_PRIME_WMG_CONVERSION_MODELS.includes(
                                        rateCardConversionModels[0].ratchetModel as RatchetModelsEnum
                                    )
                                  ? PRIME_COMBINED_CONVERSION_WMG
                                  : COMBINED_PRIME_UMG_FLOOR_CONVERSION_MODELS.includes(
                                          rateCardConversionModels[0].ratchetModel as RatchetModelsEnum
                                      )
                                    ? PRIME_COMBINED_CONVERSION_UMG
                                    : rateCardConversionModels[0].ratchetModel
                        }
                        onChange={handleConversionModelChange}
                        width={500}
                        data-testid="conversion-model-select"
                    >
                        {Object.entries(groupedConversionModels).map(([name, ratchetModels], index) => {
                            const label =
                                name === PRIME_COMBINED_CONVERSION_WMG
                                    ? 'WMG Prime Conversion Ratchet Profile'
                                    : name === PRIME_COMBINED_CONVERSION_UMG
                                      ? 'UMG Prime Conversion Floor Ratchet Profile'
                                      : (ratchetModels[0].description ?? '');
                            const value = name;

                            return <SelectOption key={index} label={label} value={value} />;
                        })}
                    </Select>
                    {rateCardConversionModels?.map((model, index) => (
                        <Column>
                            <Column spacing="300">
                                <Text type="h300">Model name</Text>
                                <Text>{model.ratchetModel ?? '-'}</Text>
                            </Column>
                            <Row alignmentVertical="top">
                                <Column spacing="300" width={250}>
                                    <Text type="h300">Rate tiers</Text>
                                    <Table headerRows={1} showStripes={false} showDividers={true}>
                                        <TableRow>
                                            <TableCell>No</TableCell>
                                            <TableCell>Threshold</TableCell>
                                        </TableRow>
                                        {!isNil(rateCardConversionModels) &&
                                        !isEmpty(conversionModelsThresholdsMap[model.ratchetModel!]) ? (
                                            conversionModelsThresholdsMap[model.ratchetModel!].map((ratchet, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{getThresholdDisplayName(ratchet)}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell columnSpan={2}>
                                                    <Text type="b200">No ratchets</Text>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Table>
                                </Column>
                                <AddConversionModelDefinitionsTable
                                    ratchetDefinitions={
                                        isUndefined(rateCardConversionModels) ? undefined : model.ratchetDefinitions
                                    }
                                    dispatchRateCardValues={dispatchRateCardValues}
                                />
                            </Row>
                            {index !== rateCardConversionModels.length - 1 && <Divider />}
                        </Column>
                    ))}
                </>
            ) : (
                <Text data-testid="no-conversion-model-text">No conversion model</Text>
            )}
        </ContractTermSection>
    );
};
